import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/homepage/Navbar";
import HomepageFooter from "@/components/homepage/HomepageFooter";
import HomepageLeadModal from "@/components/homepage/HomepageLeadModal";
import HomepageStickyCta from "@/components/homepage/HomepageStickyCta";
import NewsletterMoreIssues from "@/components/newsletter/NewsletterMoreIssues";
import {
  formatArchiveDate,
  postTimestamp,
  variedReadMinutes,
} from "@/components/newsletter/newsletter-post-utils";
import { fetchNewsletterPostBySlug, type NewsletterPost } from "@/lib/cms-newsletter";
import { sanitizeBeehiivWebHtml } from "@/lib/sanitize-beehiiv-html";

function authorInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

type NewsletterPostContentProps = {
  slug: string;
};

function NewsletterPostContent({ slug }: NewsletterPostContentProps) {
  const [post, setPost] = useState<NewsletterPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [leadModal, setLeadModal] = useState<{ open: boolean; email: string }>({
    open: false,
    email: "",
  });

  const openLeadModal = (email: string) => {
    setLeadModal({ open: true, email });
  };

  const closeLeadModal = () => {
    setLeadModal({ open: false, email: "" });
  };

  useEffect(() => {
    let cancelled = false;
    fetchNewsletterPostBySlug(slug)
      .then((data) => {
        if (cancelled) return;
        if (!data) setError("Post not found.");
        else setPost(data);
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load post.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const htmlContent = useMemo(
    () => (post?.body ? sanitizeBeehiivWebHtml(post.body) : null),
    [post],
  );

  if (loading && !post) {
    return (
      <main
        className="flex min-h-svh w-full flex-col bg-black pb-24 text-white max-md:pb-[calc(5rem+env(safe-area-inset-bottom,0px))]"
        style={{ fontFamily: "var(--font-fira)" }}
      >
        <Navbar />
        <div className="mx-auto w-[90%] max-w-3xl py-16 md:w-[80%]">
          <div className="h-10 w-3/4 animate-pulse rounded-lg bg-white/10" />
          <div className="mt-6 h-6 w-1/2 animate-pulse rounded-lg bg-white/10" />
          <div className="mt-10 aspect-video w-full animate-pulse rounded-2xl bg-white/[0.06]" />
        </div>
      </main>
    );
  }

  if (error || !post) {
    return (
      <main
        className="flex min-h-svh w-full flex-col bg-black pb-24 text-white"
        style={{ fontFamily: "var(--font-fira)" }}
      >
        <Navbar />
        <div className="mx-auto w-[90%] max-w-2xl py-16 md:w-[80%]">
          <p className="text-white/70">{error ?? "Post not found."}</p>
          <Link
            to="/newsletter"
            className="mt-6 inline-block text-sm text-white/80 underline decoration-white/30 underline-offset-4 transition hover:text-white"
          >
            ← Back to newsletter
          </Link>
        </div>
      </main>
    );
  }

  const ts = postTimestamp(post);
  const dateLabel = formatArchiveDate(ts);
  const readMins = variedReadMinutes(post);
  const subtitleText = (post.excerpt ?? "").trim();
  const primaryAuthor = post.authors?.split(",")?.[0]?.trim() || "Staying Ahead";

  return (
    <main
      className="flex min-h-svh w-full flex-col bg-black pb-24 text-white max-md:pb-[calc(5rem+env(safe-area-inset-bottom,0px))]"
      style={{ fontFamily: "var(--font-fira)" }}
    >
      <Navbar />

      <article
        id="newsletter-post-hero"
        className="mx-auto w-[90%] max-w-3xl md:w-[80%]"
      >
        <Link
          to="/newsletter"
          className="mt-6 inline-flex items-center gap-1.5 text-[13px] text-white/55 transition hover:text-white md:mt-8 md:text-sm"
        >
          <span aria-hidden>←</span> Back to newsletter
        </Link>

        <header className="font-fira mt-8 md:mt-10">
          <h1 className=" text-[28px] font-medium leading-tight tracking-tight text-[#FFFFFF] md:text-[36px]">
            {post.title}
          </h1>
          {subtitleText ? (
            <p className="mt-1 leading-relaxed text-white/90 md:text-base">
              {subtitleText}
            </p>
          ) : null}
          <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-white/90 md:text-sm">
            {dateLabel ? (
              <time
                dateTime={
                  ts != null ? new Date(ts * 1000).toISOString() : undefined
                }
                className="tabular-nums"
              >
                {dateLabel}
              </time>
            ) : null}
            {dateLabel ? (
              <span className="text-white/35" aria-hidden>
                &middot;
              </span>
            ) : null}
            <span>{readMins} min read</span>
          </div>

          <div className="mt-8 flex items-center gap-3">
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-[#FFFFFF52] bg-white/10 text-sm font-semibold text-white"
              aria-hidden
            >
              {authorInitials(primaryAuthor)}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-normal text-white/90">By {primaryAuthor}</p>
              <p className="text-[13px] text-white/70">Staying Ahead</p>
            </div>
          </div>
        </header>

        {post.thumbnailUrl ? (
          <div className="mt-10 overflow-hidden rounded-2xl border border-[#FFFFFF52]">
            <img
              src={post.thumbnailUrl}
              alt=""
              className="aspect-[16/10] w-full object-cover md:aspect-[2/1]"
              loading="eager"
              decoding="async"
            />
          </div>
        ) : null}

        {htmlContent ? (
          <>
            <style>{`
              .nl-post-content--bare.newsletter-detail-content,
              .nl-post-content--bare.newsletter-detail-content * {
                font-family: 'Fira Sans', system-ui, sans-serif !important;
              }
              .nl-post-content--bare.newsletter-detail-content,
              .nl-post-content--bare.newsletter-detail-content p,
              .nl-post-content--bare.newsletter-detail-content li,
              .nl-post-content--bare.newsletter-detail-content ul,
              .nl-post-content--bare.newsletter-detail-content ol,
              .nl-post-content--bare.newsletter-detail-content span,
              .nl-post-content--bare.newsletter-detail-content blockquote,
              .nl-post-content--bare.newsletter-detail-content td,
              .nl-post-content--bare.newsletter-detail-content th,
              .nl-post-content--bare.newsletter-detail-content div {
                font-weight: 400 !important;
                font-size: 16px !important;
                line-height: 1.6 !important;
                color: #FFFFFF85 !important;
              }
              .nl-post-content--bare.newsletter-detail-content h1,
              .nl-post-content--bare.newsletter-detail-content h2,
              .nl-post-content--bare.newsletter-detail-content h3,
              .nl-post-content--bare.newsletter-detail-content h4,
              .nl-post-content--bare.newsletter-detail-content h5,
              .nl-post-content--bare.newsletter-detail-content h6 {
                font-weight: 500 !important;
                font-size: 28px !important;
                line-height: 1.3 !important;
                color: #FFFFFF !important;
              }
              .nl-post-content--bare.newsletter-detail-content a {
                color: inherit !important;
                text-decoration: underline;
                text-underline-offset: 2px;
              }
              .nl-post-content--bare.newsletter-detail-content strong,
              .nl-post-content--bare.newsletter-detail-content b {
                font-weight: 600 !important;
                color: #FFFFFF !important;
              }
            `}</style>
            <div
              className="nl-post-content nl-post-content--bare nl-post-content--article-tail newsletter-detail-content"
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          </>
        ) : (
          <div className="mt-10 rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center text-sm text-white/55">
            No article body available for this issue.
          </div>
        )}
      </article>

      <NewsletterMoreIssues excludeSlug={slug} />

      <HomepageFooter
        hideTopBorder
        className="mt-5 md:mt-6"
        onEmailContinue={openLeadModal}
      />
      <HomepageStickyCta
        heroSectionId="newsletter-post-hero"
        ctaSectionId="homepage-cta"
        marketingBasePath="/newsletter"
        activePathPrefixes={["/newsletter"]}
        onEmailContinue={openLeadModal}
      />
      <HomepageLeadModal
        open={leadModal.open}
        initialEmail={leadModal.email}
        onClose={closeLeadModal}
      />
    </main>
  );
}

export default function NewsletterPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const safeSlug = slug?.trim() ?? "";

  if (!safeSlug) {
    return (
      <main
        className="flex min-h-svh w-full flex-col bg-black pb-24 text-white"
        style={{ fontFamily: "var(--font-fira)" }}
      >
        <Navbar />
        <div className="mx-auto w-[90%] max-w-2xl py-16 md:w-[80%]">
          <p className="text-white/70">Invalid newsletter link.</p>
          <Link
            to="/newsletter"
            className="mt-6 inline-block text-sm text-white/80 underline decoration-white/30 underline-offset-4 transition hover:text-white"
          >
            ← Back to newsletter
          </Link>
        </div>
      </main>
    );
  }

  return <NewsletterPostContent key={safeSlug} slug={safeSlug} />;
}